'use client';

import React, {
  createContext,
  useState,
  useCallback,
  useMemo,
} from 'react';
import { CrossDark } from '../../assets/icons';
import { useTranslations } from 'next-intl';
import styles from './Toast.module.css';

export interface Toast {
  id: string;
  message: string;
  type: 'error' | 'success' | 'info';
  duration?: number;
}

interface ToastContextType {
  showToast: (
    message: string,
    type?: 'error' | 'success' | 'info',
    duration?: number,
  ) => void;
  hideToast: (id: string) => void;
  clearAllToasts: () => void;
}

const initialToastContext: ToastContextType = {
  showToast: () => {
    // No-op
  },
  hideToast: () => {
    // No-op
  },
  clearAllToasts: () => {
    // No-op
  },
};

export const ToastContext =
  createContext<ToastContextType>(initialToastContext);

export const ToastProvider = ({ children }: { children: React.ReactNode }) => {
  const [toasts, setToasts] = useState<Toast[]>([]);

  const showToast = useCallback(
    (
      message: string,
      type: 'error' | 'success' | 'info' = 'error',
      duration = 5000,
    ) => {
      const id = `toast-${Date.now()}-${Math.random()
        .toString(36)
        .substr(2, 9)}`;
      const newToast: Toast = { id, message, type, duration };

      setToasts((prev) => [...prev, newToast]);

      // Auto dismiss
      if (duration > 0) {
        setTimeout(() => {
          hideToast(id);
        }, duration);
      }
    },
    [],
  );

  const hideToast = useCallback((id: string) => {
    setToasts((prev) => prev.filter((toast) => toast.id !== id));
  }, []);

  const clearAllToasts = useCallback(() => {
    setToasts([]);
  }, []);

  const contextValue = useMemo(
    () => ({ showToast, hideToast, clearAllToasts }),
    [showToast, hideToast, clearAllToasts],
  );

  return (
    <ToastContext.Provider value={contextValue}>
      {children}
      <div className={styles.toastContainer}>
        {toasts.map((toast, index) => (
          <ToastItem
            key={toast.id}
            toast={toast}
            index={index}
            total={toasts.length}
            onClose={() => hideToast(toast.id)}
          />
        ))}
      </div>
    </ToastContext.Provider>
  );
};

const ToastItem = ({
  toast,
  index,
  total,
  onClose,
}: {
  toast: Toast;
  index: number;
  total: number;
  onClose: () => void;
}) => {
  const t = useTranslations('common');

  const marginBottom =
    total === 1 || index === 0 ? '0' : `-${Math.min(index * 6, 18)}px`;

  const transform =
    total === 1 || index === 0
      ? 'translateX(0) translateY(0) scale(1)'
      : `translateX(0) translateY(${Math.min(index * 8, 24)}px) scale(${Math.max(
          0.85,
          1 - index * 0.05,
        )})`;

  const opacity =
    total === 1 || index === 0 ? '1' : Math.max(0.6, 1 - index * 0.15);

  const toastStyle = {
    ['--toast-margin-bottom' as string]: marginBottom,
    ['--toast-transform' as string]: transform,
    ['--toast-opacity' as string]: opacity,
    ['--toast-z-index' as string]: 1000 - index,
  } as React.CSSProperties;

  return (
    <div className={styles.styledToast} data-type={toast.type} style={toastStyle}>
      <div className={styles.toastContent}>
        <div className={styles.toastMessage}>{toast.message}</div>
        <button
          className={styles.toastClose}
          onClick={onClose}
          aria-label={t('toast.button.ariaLabel.closeNotification')}
        >
          <CrossDark fill={'#ffffff'} />
        </button>
      </div>
    </div>
  );
};
